import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  TextField,
  Button,
  Typography,
  Grid,
  Box,
  FormControlLabel,
  Checkbox
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const AddonForm = ({ 
  open, 
  onClose, 
  currentItem,
  onSubmit,
  userId
}) => {
  const [errors, setErrors] = React.useState({});
  const [formData, setFormData] = React.useState({
    user_type: "",
    charges: "",
    actual_price: "",
    buyer_no_unlimited: false,
    no_of_properties_unlimited: false,
    no_of_liked_data_unlimited: false,
    matching_enquiry_unlimited: false,
    buyer_no: 0,
    no_of_properties: 0,
    no_of_liked_data: 0,
    matching_enquiry: 0
  });

  React.useEffect(() => {
    if (currentItem) {
      setFormData({
        user_type: currentItem.user_type || "",
        charges: currentItem.charges || "",
        actual_price: currentItem.actual_price || "",
        buyer_no_unlimited: currentItem.buyer_no_unlimited || false,
        no_of_properties_unlimited: currentItem.no_of_properties_unlimited || false,
        no_of_liked_data_unlimited: currentItem.no_of_liked_data_unlimited || false,
        matching_enquiry_unlimited: currentItem.matching_enquiry_unlimited || false,
        buyer_no: currentItem.buyer_no || 0,
        no_of_properties: currentItem.no_of_properties || 0,
        no_of_liked_data: currentItem.no_of_liked_data || 0,
        matching_enquiry: currentItem.matching_enquiry || 0
      });
    } else {
      setFormData({
        user_type: "",
        charges: "",
        actual_price: "",
        buyer_no_unlimited: false,
        no_of_properties_unlimited: false,
        no_of_liked_data_unlimited: false,
        matching_enquiry_unlimited: false,
        buyer_no: 0,
        no_of_properties: 0,
        no_of_liked_data: 0,
        matching_enquiry: 0
      });
    }
    setErrors({});
  }, [currentItem, open]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error on change
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.user_type) {
      newErrors.user_type = "User Type is required";
    }

    if (!formData.charges || Number(formData.charges) <= 0) {
      newErrors.charges = "Price is required";
    }

    if (!formData.actual_price || Number(formData.actual_price) <= 0) {
      newErrors.actual_price = "Actual Price is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    const payload = {
      user_id: userId,
      ...formData,
      charges: Number(formData.charges),
      actual_price: Number(formData.actual_price),
      // charges: Math.round(Number(formData.charges) * 1.18),           // ✅ store with GST
      // actual_price: Math.round(Number(formData.actual_price) * 1.18), // ✅ store with GST
      buyer_no: formData.buyer_no_unlimited ? null : Number(formData.buyer_no),
      no_of_properties: formData.no_of_properties_unlimited ? null : Number(formData.no_of_properties),
      no_of_liked_data: formData.no_of_liked_data_unlimited ? null : Number(formData.no_of_liked_data),
      matching_enquiry: formData.matching_enquiry_unlimited ? null : Number(formData.matching_enquiry)
    };

    onSubmit(payload);
    onClose();
  };

  const isBuyerOrTenant = formData.user_type === "Buyer" || formData.user_type === "Tenant";
  const isSellerOrBuilder = formData.user_type === "Individual Owner/Builder" || formData.user_type === "Landlord";
  const isBankAuction = formData.user_type === "Bank Auction";
  const isAgent = formData.user_type === "Agent";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">
          {currentItem ? 'Edit AddOn Plan' : 'Add New AddOn Plan'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>

            {/* User Type */}
            <TextField
              margin="normal"
              fullWidth
              select
              label="User Type"
              name="user_type"
              value={formData.user_type}
              onChange={handleChange}
              required
              error={!!errors.user_type}
              helperText={errors.user_type}
            >
              <MenuItem value="Buyer">Buyer</MenuItem>
              <MenuItem value="Tenant">Tenant</MenuItem>
              <MenuItem value="Individual Owner/Builder">Individual Owner/Builder</MenuItem>
              <MenuItem value="Landlord">Landlord</MenuItem>
              <MenuItem value="Agent">Agent</MenuItem>
              <MenuItem value="Bank Auction">Bank Auction</MenuItem>
            </TextField>

            {/* Price */}
            <TextField
              margin="normal"
              fullWidth
              label="Price (Charges)"
              name="charges"
              type="number"
              value={formData.charges}
              onChange={handleChange}
              required
              inputProps={{ min: 0 }}
              error={!!errors.charges}
              helperText={errors.charges}
            />
            {Number(formData.charges) > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                Will be stored as:{' '}
                <strong style={{ color: '#1976d2' }}>
                  ₹{Math.round(Number(formData.charges) * 1.18)}
                </strong>{' '}
                (incl. 18% GST)
              </Typography>
            )}

            {/* Actual Price */}
            <TextField
              margin="normal"
              fullWidth
              label="Actual Price (Charges)"
              name="actual_price"
              type="number"
              value={formData.actual_price}
              onChange={handleChange}
              required
              inputProps={{ min: 0 }}
              error={!!errors.actual_price}
              helperText={errors.actual_price}
            />
            {Number(formData.actual_price) > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                Will be stored as:{' '}
                <strong style={{ color: '#1976d2' }}>
                  ₹{Math.round(Number(formData.actual_price) * 1.18)}
                </strong>{' '}
                (incl. 18% GST)
              </Typography>
            )}

          </Grid>

          <Grid item xs={12} md={6}>

            {/* Buyer / Tenant / Agent / Seller fields */}
            {(isBuyerOrTenant || isAgent || isSellerOrBuilder) && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.no_of_properties_unlimited}
                      onChange={handleChange}
                      name="no_of_properties_unlimited"
                      color="primary"
                    />
                  }
                  label={isBuyerOrTenant ? "Unlimited Access to Owner Details" : "Unlimited Uploading Properties"}
                />
                {!formData.no_of_properties_unlimited && (
                  <TextField
                    margin="normal"
                    fullWidth
                    type="number"
                    label={isBuyerOrTenant ? "Owner Details Access Limit" : "Uploading Property Limit"}
                    name="no_of_properties"
                    value={formData.no_of_properties}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                )}
              </>
            )}

            {/* Bank Auction fields */}
            {isBankAuction && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.no_of_properties_unlimited}
                      onChange={handleChange}
                      name="no_of_properties_unlimited"
                      color="primary"
                    />
                  }
                  label="Unlimited Uploading Properties"
                />
                {!formData.no_of_properties_unlimited && (
                  <TextField
                    margin="normal"
                    fullWidth
                    type="number"
                    label="Uploading Property Limit"
                    name="no_of_properties"
                    value={formData.no_of_properties}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                )}
              </>
            )}

            {/* Seller / Builder / Landlord / Agent fields */}
            {(isSellerOrBuilder || isAgent) && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.no_of_liked_data_unlimited}
                      onChange={handleChange}
                      name="no_of_liked_data_unlimited"
                      color="primary"
                    />
                  }
                  label="Unlimited Wishlist Data"
                />
                {!formData.no_of_liked_data_unlimited && (
                  <TextField
                    margin="normal"
                    fullWidth
                    type="number"
                    label="Wishlist Data Limit"
                    name="no_of_liked_data"
                    value={formData.no_of_liked_data}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.matching_enquiry_unlimited}
                      onChange={handleChange}
                      name="matching_enquiry_unlimited"
                      color="primary"
                    />
                  }
                  label="Unlimited Matching Buyers"
                />
                {!formData.matching_enquiry_unlimited && (
                  <TextField
                    margin="normal"
                    fullWidth
                    type="number"
                    label="Matching Buyers Limit"
                    name="matching_enquiry"
                    value={formData.matching_enquiry}
                    onChange={handleChange}
                    inputProps={{ min: 0 }}
                  />
                )}
              </>
            )}

          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {currentItem ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddonForm;